import axios from './axios-with-authentication';
import * as backend from './backend-types';
import { DeviceType } from '../types';
import {
  DEVICE_ADAPTER_API_BASE_PATH,
  HEALTHDOT_CODE_REGEX,
  HEALTHDOT_SERIAL_REGEX,
  VIQTOR_SERIAL_REGEX,
  VIQTOR_UDI_REGEX,
} from './constants';
import { fetchAllPagedContent } from './pagination';

export const DEVICE_PAGE_SIZE = 100;
// The listing is paged, so a client-side match over one page would miss a device on the
// next. This bound only stops a broken `last` flag from looping for ever.
export const MAX_DEVICE_PAGES = 50;

export type DeviceListingParams = {
  hcoId: string;
  /**
   * Optional window, in hours, narrowing the listing to devices whose assignment began
   * recently. The overview wants that; anything resolving a scanned device must not pass
   * it, or a device paired earlier reads as if it does not exist.
   */
  pairedSince?: number;
};

/**
 * Reads the device-adapter device listing, following its pages to the end.
 *
 * This is the listing that states each device's type. The portal listing does not, which
 * is why every consumer here reads the device-adapter instead.
 */
export async function fetchDeviceListing(
  params: DeviceListingParams,
): Promise<backend.DeviceAdapterDeviceListEntry[]> {
  return fetchAllPagedContent<
    backend.DeviceAdapterDeviceListEntry,
    backend.DeviceAdapterDevicePage
  >({
    fetchPage: async (pageNumber, pageSize) => {
      const { data } = await axios.get<backend.DeviceAdapterDevicePage>(
        `${DEVICE_ADAPTER_API_BASE_PATH}/devices`,
        {
          params: {
            ...params,
            page: pageNumber,
            size: pageSize,
          },
        },
      );
      return data;
    },
    initialPageSize: DEVICE_PAGE_SIZE,
    maxPages: MAX_DEVICE_PAGES,
  });
}

/** Anything carrying the device type the backend reports, alongside its serial number. */
export type DeviceTypeSource = {
  deviceType?: string | null;
  serialNumber?: string | null;
};

/**
 * Resolves what kind of device this is.
 *
 * The listing states the type, so the serial patterns are only the fallback for a device
 * it does not classify -- and a weak one, because the two serial spaces overlap: a
 * HealthDot serial is 16 alphanumerics, and a viQtor serial of that length matches too.
 * Prefer a caller that has the listing entry over one that only has a serial number.
 */
export function resolveDeviceType(
  device?: DeviceTypeSource,
  fallbackSerialNumber?: string,
): DeviceType {
  const reported = String(device?.deviceType ?? '')
    .trim()
    .toUpperCase();
  if (reported === 'HEALTHDOT') return DeviceType.Healthdot;
  if (reported === 'VIQTOR') return DeviceType.ViQtor;

  const serial = device?.serialNumber?.trim() || fallbackSerialNumber?.trim();
  if (!serial) return DeviceType.Unknown;

  if (HEALTHDOT_CODE_REGEX.test(serial) || HEALTHDOT_SERIAL_REGEX.test(serial)) {
    return DeviceType.Healthdot;
  }

  if (VIQTOR_UDI_REGEX.test(serial) || VIQTOR_SERIAL_REGEX.test(serial)) {
    return DeviceType.ViQtor;
  }

  return DeviceType.Unknown;
}
